import React, { useMemo, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import Drawer from '../../shared/ui/Drawer.js';
import { CONTROL_INPUT, requestTypeLabel } from './scheduler-ui.js';
import { dayStatusPresentation, hasConstraintViolation, TERMINAL_COLORS } from './scheduler-grid-ui.js';
import { resolveSchedulerTerminal } from './use-scheduler-read-model.js';
import type { SchedulerDriver, GridData, GridRow as GridRowType, GridDay, TerminalOption } from './use-scheduler-read-model.js';
import { useSchedulerWrite } from './use-scheduler-write.js';

interface SelectedDayCell {
  employee: SchedulerDriver;
  day: GridDay;
  isoDay: string;
}

interface GridRowProps {
  row: GridRowType;
  days: string[];
  rosterById: Record<string, SchedulerDriver>;
  onOpenDriver: (driver: SchedulerDriver) => void;
  onOpenDayCell: (driver: SchedulerDriver, day: GridDay, isoDay: string) => void;
}

function GridRow({ row, days, rosterById, onOpenDriver, onOpenDayCell }: GridRowProps) {
  const driver = rosterById[row.employee.id] || row.employee;

  return (
    <tr>
      <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.borderSoft}`, whiteSpace: 'nowrap' }}>
        <button type="button" onClick={() => onOpenDriver(driver)} style={{ background: 'transparent', border: 'none', padding: 0, color: C.text, cursor: 'pointer', textAlign: 'left' }}>
          {row.employee.first_name} {row.employee.last_name}
        </button>
      </td>
      {row.days.map((day: GridDay, idx: number) => {
        const presentation = dayStatusPresentation(day);
        const violation = hasConstraintViolation(driver, days[idx], day);
        return (
          <td
            key={`${row.employee.id}-${days[idx]}`}
            onClick={() => onOpenDayCell(driver, day, days[idx])}
            style={{
              padding: '6px',
              textAlign: 'center',
              background: presentation.background,
              color: presentation.color,
              borderBottom: `1px solid ${C.borderSoft}`,
              border: violation ? `2px solid ${C.red}` : undefined,
              borderRadius: violation ? 4 : 0,
              fontSize: 11,
              fontWeight: violation ? 700 : 500,
              cursor: 'pointer',
            }}
            title={violation ? 'Constraint violation' : 'Click to manage this day'}
          >
            {presentation.label}
          </td>
        );
      })}
    </tr>
  );
}

interface SchedulerWeeklyGridProps {
  gridData: GridData | null;
  roster: SchedulerDriver[];
  search: string;
  selectedTerminalId: string;
  terminalOptions: TerminalOption[];
  onOpenDriver: (driver: SchedulerDriver) => void;
  onGridChange: () => void;
}

function DayOverrideDrawer({
  selected,
  onClose,
  onSaved,
}: {
  selected: SelectedDayCell | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const { createDayOverride, submitting, error, clearError } = useSchedulerWrite(() => {
    onSaved();
  });

  if (!selected) return null;

  const { employee, day, isoDay } = selected;
  const displayName = [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim() || `Driver ${employee.driver_id || ''}`.trim();
  const friendlyDate = new Date(`${isoDay}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const alreadyOff = day.status === 'time_off' || day.status === 'day_override';
  const statusLabel = day.status === 'day_override' ? 'Day override' : day.status === 'time_off' ? requestTypeLabel(day.type) : 'Scheduled';

  async function handleSave() {
    clearError();
    setResult(null);
    const outcome = await createDayOverride(employee.id, isoDay, reason.trim() || undefined);
    if (!outcome.success) return;
    setResult(outcome.alreadyOff ? 'That day is already marked off.' : 'Day marked off.');
    if (!outcome.alreadyOff) setReason('');
    if (!outcome.alreadyOff) {
      onClose();
    }
  }

  return (
    <Drawer open={true} onClose={onClose} title="Day override" width={400}>
      <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{displayName}</div>
      <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>{employee.driver_id || 'No driver ID'}</div>
      <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 16 }}>{friendlyDate}</div>

      <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, marginBottom: 12 }}>
        <div style={{ color: C.gold, fontSize: 13, marginBottom: 6 }}>Current day status</div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>{statusLabel}</div>
        {day.note ? <div style={{ color: C.textMuted, fontSize: 11, marginTop: 6 }}>{day.note}</div> : null}
      </div>

      {!alreadyOff ? (
        <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
          <div style={{ color: C.gold, fontSize: 13, marginBottom: 6 }}>Mark day off</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 10 }}>
            This creates a one-day schedule override and leaves the rest of the week intact.
          </div>
          <label style={{ color: C.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
            Note (optional)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Doctor appointment, personal conflict, etc."
            style={{ ...CONTROL_INPUT, marginBottom: 10 }}
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              background: C.red,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Saving...' : 'Mark day off'}
          </button>
        </div>
      ) : (
        <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, color: C.textMuted, fontSize: 12 }}>
          This date is already off in schedule truth, so no weekly rewrite is needed.
        </div>
      )}

      {result ? <div style={{ color: C.green, fontSize: 12, marginTop: 10 }}>{result}</div> : null}
      {error ? <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{error}</div> : null}
    </Drawer>
  );
}

export default function SchedulerWeeklyGrid({ gridData, roster, search, selectedTerminalId, terminalOptions, onOpenDriver, onGridChange }: SchedulerWeeklyGridProps) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [selectedDayCell, setSelectedDayCell] = useState<SelectedDayCell | null>(null);
  const days = gridData?.days || [];
  const grid = gridData?.grid || [];
  const rosterById: Record<string, SchedulerDriver> = Object.fromEntries((roster || []).map((driver: SchedulerDriver) => [String(driver.id), driver]));

  const filteredGrid = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    if (!query) return grid;
    return grid.filter((row: GridRowType) => {
      const name = `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.toLowerCase();
      const driverId = String(row.employee.driver_id || rosterById[row.employee.id]?.driver_id || '').toLowerCase();
      return name.includes(query) || driverId.includes(query);
    });
  }, [grid, rosterById, search]);

  const terminalFilteredGrid = useMemo(() => {
    if (!selectedTerminalId || selectedTerminalId === 'all') return filteredGrid;
    return filteredGrid.filter((row: GridRowType) => {
      const driver = rosterById[row.employee.id] || row.employee;
      const resolvedTerminal = resolveSchedulerTerminal(driver);
      return String(resolvedTerminal?.id || '') === String(selectedTerminalId);
    });
  }, [filteredGrid, rosterById, selectedTerminalId]);

  if (!gridData) {
    return null;
  }

  if (!terminalFilteredGrid.length) {
    const selectedTerminalName = terminalOptions.find((terminal) => String(terminal.id) === String(selectedTerminalId))?.name;
    return (
      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
          Weekly grid read shell
        </div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>
          No scheduler grid rows returned for the current filter{selectedTerminalName ? ` (${selectedTerminalName})` : ''}.
        </div>
      </div>
    );
  }

  const todayIso = new Date().toISOString().split('T')[0];
  const terminalGroups: Record<string, GridRowType[]> = {};

  terminalFilteredGrid.forEach((row: GridRowType) => {
    const termCounts: Record<string, number> = {};
    const driver = rosterById[row.employee.id] || row.employee;
    const resolvedTerminal = resolveSchedulerTerminal(driver);
    row.days.forEach((day: GridDay) => {
      if (day.status === 'terminal' && day.terminal?.name) {
        termCounts[day.terminal.name] = (termCounts[day.terminal.name] || 0) + 1;
      }
    });
    let topTerminal = resolvedTerminal?.name || 'Home / Unassigned';
    let topCount = 0;
    Object.entries(termCounts).forEach(([name, count]) => {
      if (count > topCount) {
        topTerminal = name;
        topCount = count as number;
      }
    });
    if (!terminalGroups[topTerminal]) terminalGroups[topTerminal] = [];
    terminalGroups[topTerminal].push(row);
  });

  const groupNames = Object.keys(terminalGroups).sort((a, b) => {
    if (a === 'Home / Unassigned') return 1;
    if (b === 'Home / Unassigned') return -1;
    return a.localeCompare(b);
  });

  function toggleGroup(groupName: string) {
    setOpenGroups((current) => ({ ...current, [groupName]: current[groupName] === false ? true : false }));
  }

  function openDayCell(driver: SchedulerDriver, day: GridDay, isoDay: string) {
    setSelectedDayCell({
      employee: driver,
      day: {
        ...day,
        date: isoDay,
      },
      isoDay,
    });
  }

  return (
    <>
      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
          Weekly grid
        </div>
        <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 10 }}>
          Click a day cell to mark that employee off without changing the rest of the week.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textMuted, borderBottom: `1px solid ${C.borderSoft}`, minWidth: 160 }}>Driver</th>
                {days.map((day: string) => (
                  <th key={day} style={{ padding: '8px 6px', color: day === todayIso ? C.gold : C.textMuted, borderBottom: `1px solid ${C.borderSoft}`, minWidth: 72, textAlign: 'center' }}>
                    {new Date(`${day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                    <br />
                    <span style={{ fontSize: 10, color: C.textMuted }}>{day.slice(5)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupNames.map((groupName) => {
                const rows = terminalGroups[groupName];
                const groupColor = groupName === 'Home / Unassigned' ? 'rgba(255,255,255,0.03)' : TERMINAL_COLORS[rows?.[0]?.days?.find((day: GridDay) => day.terminal?.name === groupName)?.terminal?.number || ''] || 'rgba(255,255,255,0.03)';
                const isOpen = openGroups[groupName] !== false;
                return (
                  <React.Fragment key={groupName}>
                    <tr style={{ background: groupColor }}>
                      <td colSpan={days.length + 1} style={{ padding: '8px 12px', color: C.text, borderBottom: `1px solid ${C.borderSoft}`, fontSize: 13, fontWeight: 600 }}>
                        <button type="button" onClick={() => toggleGroup(groupName)} style={{ background: 'transparent', border: 'none', padding: 0, color: C.text, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          {isOpen ? '▾' : '▸'} {groupName}
                        </button>
                        <span style={{ color: C.textMuted, fontSize: 11, fontWeight: 400, marginLeft: 8 }}>{rows.length} drivers</span>
                      </td>
                    </tr>
                    {isOpen ? rows.map((row: GridRowType) => (
                      <GridRow
                        key={row.employee.id}
                        row={row}
                        days={days}
                        rosterById={rosterById}
                        onOpenDriver={onOpenDriver}
                        onOpenDayCell={openDayCell}
                      />
                    )) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <DayOverrideDrawer
        selected={selectedDayCell}
        onClose={() => setSelectedDayCell(null)}
        onSaved={onGridChange}
      />
    </>
  );
}
